import 'date-fns';
import React, {useRef, useState} from 'react';
import DateFnsUtils from '@date-io/date-fns';
import {Box, Button, Grid, MenuItem, Select, Typography, makeStyles, FormHelperText} from '@material-ui/core';
import {MuiPickersUtilsProvider, KeyboardDatePicker} from '@material-ui/pickers';
import {observer} from 'mobx-react-lite';
import {ParameterStore} from '@/Core/mobx/ParameterStore/ParameterStore';
import InputText from '../../../UI/EmcApplicationQuestions/inputs/core/InputText';
import {z} from 'zod';
import {Controller, useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {useCommonModalStyles} from '@/Agent/Quotes/InstantAppModalPages/common/modalStyles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import csx from 'classnames';
import Divider from '@material-ui/core/Divider';
import {format} from 'date-fns';
import FixedNavigation from '@/Agent/Quotes/InstantAppModalPages/common/FixedNavigation';
import StickyTopNavigation from '@/Agent/Quotes/InstantAppModalPages/common/StickyTopNavigation';
import {ContactFormStore} from '@/Agent/ContactForm/ContactFormStore/ContactFormStore';
import {Autocomplete, useJsApiLoader} from '@react-google-maps/api';
import {STATE_OPTIONS} from '@/Agent/constants';
import {DateRange} from '@material-ui/icons';

interface LastDoctorVisitProps {
    parameterStore: ParameterStore;
    contactFormStore: ContactFormStore;
    onExit: () => void;
    onNext: () => void;
    onBack?: () => void;
}

const useStyles = makeStyles(() => ({
    buttonWrapper: {
        display: 'flex',
        columnGap: '24px',
        marginBottom: 24,
    },
    btn: {
        fontFamily: 'FredokaRegular',
        fontWeight: 400,
        fontSize: '28px',
        lineHeight: '140%',
        borderWidth: '1.75px',
        borderRadius: '8px',
        padding: '20px',
        maxWidth: 337,
        width: '100%',
        color: '#174943',
        textTransform: 'none',
        borderColor: '#174943',
        '&:hover': {
            borderWidth: '1.75px',
            borderColor: '#174943',
            backgroundColor: 'rgba(90, 226, 136, 0.04)',
        },
    },
    activeBtn: {
        backgroundColor: '#EDFCF2',
    },
    container: {
        maxWidth: 700,
        width: '100%',
        position: 'relative',
        paddingBottom: 113,
        margin: '0 auto',
    },
    title: {
        marginBottom: 40,
        marginTop: 40,
        fontFamily: 'LoraSemiBold',
        fontStyle: 'normal',
        fontWeight: 600,
        fontSize: 40,
        lineHeight: '110%',
        color: '#3F51B5',
        opacity: 0.9,
    },
}));

type Address = {
    Street: string;
    City: string;
    State: string;
    ZipCode: string;
    Country: string;
    DefaultZipCode: string;
    DefaultState: string;
};

const LIBRARIES: ['places'] = ['places'];

const LastDoctorVisit: React.FC<LastDoctorVisitProps> = observer(
    ({onNext, onBack, onExit, parameterStore, contactFormStore}) => {
        const classes = useStyles();
        const commonClasses = useCommonModalStyles();
        const {approval, onValueChangedPreApproval} = parameterStore;
        const nameParts = approval.specialistName.value.trim().split(' ');
        const visitDate = new Date(approval.lastDoctorVisitLastVisited.value);
        const [showFollowUpQuestion, setShowFollowUpQuestion] = useState(false);
        const formSchema = z
            .object({
                hasSeenPrimaryCare: z
                    .boolean()
                    .nullable()
                    .refine((value) => value !== null),
                reasonForVisit: z.string().optional(),
                providerAddressLine: z.string().optional(),
                visitDate: z
                    .date()
                    .nullable()
                    .optional()
                    .refine((date) => !date || date <= new Date(), {
                        message: 'Date must not be in the future.',
                    }),
                providerCity: z.string().optional(),
                providerState: z.string().optional(),
                providerZipCode: z
                    .string()
                    .optional()
                    .refine((value) => !value || /^\d{5}(-\d{4})?$/.test(value.replace(/\s/g, '')), {
                        message: 'Zip must be in a valid format',
                    }),
                providerFirstName: z.string().optional(),
                providerLastName: z.string().optional(),
                lastDoctorVisitReasonAddnTests: z.boolean().nullable().optional(),
                lastDoctorVisitReasonAddnTestsDetails: z.string().nullable().optional(),
            })
            .superRefine((data, ctx) => {
                const {
                    hasSeenPrimaryCare,
                    reasonForVisit,
                    providerState,
                    providerLastName,
                    visitDate,
                    lastDoctorVisitReasonAddnTests,
                    lastDoctorVisitReasonAddnTestsDetails,
                } = data;

                if (hasSeenPrimaryCare) {
                    if (!reasonForVisit) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Reason for visit is required.',
                            path: ['reasonForVisit'],
                        });
                    }
                    if (!visitDate) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Date Visited is required.',
                            path: ['visitDate'],
                        });
                    }
                    if (!providerState) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'State is required.',
                            path: ['providerState'],
                        });
                    }
                    if (!providerLastName?.trim()) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Last Name is required.',
                            path: ['providerLastName'],
                        });
                    }
                    if (showFollowUpQuestion && lastDoctorVisitReasonAddnTests === null) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Required',
                            path: ['lastDoctorVisitReasonAddnTests'],
                        });
                    }
                    if (
                        showFollowUpQuestion &&
                        lastDoctorVisitReasonAddnTests &&
                        !lastDoctorVisitReasonAddnTestsDetails?.trim()
                    ) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Additional Details are required.',
                            path: ['lastDoctorVisitReasonAddnTestsDetails'],
                        });
                    }
                }
            });

        type FormSchema = z.infer<typeof formSchema>;

        const {watch, trigger, control, handleSubmit, setValue, formState} = useForm({
            resolver: zodResolver(formSchema),
            mode: 'onChange',
            defaultValues: {
                hasSeenPrimaryCare: approval.lastDoctorVisitLastVisited.value ? true : null,
                reasonForVisit: approval.lastDoctorVisitReason.value || '',
                providerAddressLine: approval.lastDoctorVisitStreet.value || '',
                visitDate: isNaN(visitDate.getTime()) ? null : visitDate,
                providerCity: approval.lastDoctorVisitCity.value || '',
                providerState: approval.lastDoctorVisitState.value || '',
                providerZipCode: approval.lastDoctorVisitZip.value || '',
                providerFirstName: nameParts.length > 1 ? nameParts[0] : '',
                providerLastName: nameParts.slice(1).join(' ') || nameParts[0],
                lastDoctorVisitReasonAddnTests: null,
                lastDoctorVisitReasonAddnTestsDetails: '',
            },
        });

        const {isLoaded, loadError} = useJsApiLoader({
            googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAP_API_KEY || '',
            libraries: LIBRARIES,
            language: 'en-US',
            region: 'us',
        });

        const [autocomplete, setAutocomplete] = useState<any | null>(null);
        const containerRef = useRef<HTMLDivElement | null>(null);

        const handleLoad = (autocompleteInstance: any) => {
            setAutocomplete(autocompleteInstance);
        };

        function onAddressSelect(
            address: Omit<
                Address & Partial<{placeName: string; placeTypes: string[]}>,
                'DefaultZipCode' | 'DefaultState'
            >,
        ) {
            if (address?.placeName) {
                setValue('providerAddressLine', address?.Street);
                setValue('providerCity', address?.City);
                setValue('providerState', address?.State);
                setValue('providerZipCode', address?.ZipCode);
            }
        }

        const onPlacesChanged = () => {
            const place = autocomplete?.getPlace();
            const myAddress: Omit<
                Address & Partial<{placeName: string; placeTypes: string[]}>,
                'DefaultZipCode' | 'DefaultState'
            > = {
                Street: '',
                City: '',
                State: '',
                ZipCode: '',
                Country: '',
            };
            let streetNumber = '';
            let streetName = '';

            place?.address_components?.forEach((c: {types: any[]; short_name: string; long_name: string}) => {
                switch (c.types[0]) {
                    case 'street_number':
                        streetNumber = c.short_name;
                        break;
                    case 'route':
                        streetName = c.long_name;
                        break;
                    case 'neighborhood':
                    case 'locality': // North Hollywood or Los Angeles?
                        myAddress.City = c.long_name;
                        break;
                    case 'administrative_area_level_1': //  Note some countries don't have states
                        myAddress.State = c.short_name;
                        break;
                    case 'postal_code':
                        myAddress.ZipCode = c.short_name;
                        break;
                    case 'country':
                        myAddress.Country = c.long_name;
                        break;
                    default:
                        break;
                }
            });

            myAddress.Street = `${streetNumber || ''} ${streetName || ''}`.trim();
            myAddress.placeName = place?.name;
            myAddress.placeTypes = place?.types;

            onAddressSelect(myAddress);
        };

        const onSubmit = (data: FormSchema) => {
            if (showFollowUpQuestion) {
                contactFormStore.saveForm();
                onNext();
            } else {
                const isFollowUpQuestionFlow =
                    data.reasonForVisit === 'Routine annual wellness check up' ||
                    data.reasonForVisit === 'Follow up on labwork or testing';
                if (isFollowUpQuestionFlow && !showFollowUpQuestion) setShowFollowUpQuestion(true);
                const formattedDate = data.visitDate ? format(data.visitDate, 'MM/dd/yyyy') : '';
                onValueChangedPreApproval(approval.lastDoctorVisitReason, data.reasonForVisit);
                onValueChangedPreApproval(approval.lastDoctorVisitLastVisited, formattedDate);
                onValueChangedPreApproval(approval.lastDoctorVisitStreet, data.providerAddressLine);
                onValueChangedPreApproval(approval.lastDoctorVisitCity, data.providerCity);
                onValueChangedPreApproval(approval.lastDoctorVisitState, data.providerState);
                onValueChangedPreApproval(approval.lastDoctorVisitZip, data.providerZipCode);
                onValueChangedPreApproval(
                    approval.specialistName,
                    [data.providerFirstName, data.providerLastName].filter(Boolean).join('  '),
                );
                onValueChangedPreApproval(approval.lastDoctorVisitReasonAddnTests, null);
                onValueChangedPreApproval(approval.lastDoctorVisitReasonAddnTestsDetails, '');
                setValue('lastDoctorVisitReasonAddnTests', null);
                setValue('lastDoctorVisitReasonAddnTestsDetails', '');
                if (!isFollowUpQuestionFlow) {
                    contactFormStore.saveForm();
                    onNext();
                } else setTimeout(() => trigger());
            }
        };

        const watchHasSeenPrimaryCare = watch('hasSeenPrimaryCare');
        const watchReasonForVisit = watch('reasonForVisit');
        const watchLastDoctorVisitReasonAddnTests = watch('lastDoctorVisitReasonAddnTests');

        const clearForm = () => {
            setValue('reasonForVisit', '');
            setValue('providerFirstName', '');
            setValue('providerLastName', '');
            setValue('visitDate', null);
            setValue('providerAddressLine', '');
            setValue('providerCity', '');
            setValue('providerState', '');
            setValue('providerZipCode', '');
            setValue('lastDoctorVisitReasonAddnTests', null);
            setValue('lastDoctorVisitReasonAddnTestsDetails', '');
        };

        return (
            <Box className={classes.container}>
                <StickyTopNavigation onExit={onExit} />
                <Typography className={classes.title}>
                    {showFollowUpQuestion
                        ? watchReasonForVisit === 'Routine annual wellness check up'
                            ? 'Based on this check up, were there any additional tests or evaluations performed or recommended?'
                            : 'Based on the labwork or testing, were any additional tests or evaluations performed or recommended?'
                        : 'Within the past 5 years have you seen a primary care physician or a member of the medical profession?'}
                </Typography>
                <Grid container spacing={2} component="form" onSubmit={handleSubmit(onSubmit as any)}>
                    <Grid item xs={12} classes={{root: classes.buttonWrapper}}>
                        {showFollowUpQuestion ? (
                            <Controller
                                name="lastDoctorVisitReasonAddnTests"
                                control={control}
                                render={({field}) => (
                                    <>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            classes={{
                                                root: csx(classes.btn, {
                                                    [classes.activeBtn]: field.value === true,
                                                }),
                                            }}
                                            onClick={() => {
                                                field.onChange(true);
                                            }}
                                        >
                                            Yes
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            classes={{
                                                root: csx(classes.btn, {
                                                    [classes.activeBtn]: field.value === false,
                                                }),
                                            }}
                                            onClick={() => {
                                                field.onChange(false);
                                                setValue('lastDoctorVisitReasonAddnTestsDetails', '');
                                            }}
                                        >
                                            No
                                        </Button>
                                    </>
                                )}
                            />
                        ) : (
                            <Controller
                                name="hasSeenPrimaryCare"
                                control={control}
                                render={({field}) => (
                                    <>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            classes={{
                                                root: csx(classes.btn, {
                                                    [classes.activeBtn]: field.value === true,
                                                }),
                                            }}
                                            onClick={() => {
                                                field.onChange(true);
                                            }}
                                        >
                                            Yes
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            classes={{
                                                root: csx(classes.btn, {
                                                    [classes.activeBtn]: field.value === false,
                                                }),
                                            }}
                                            onClick={() => {
                                                clearForm();
                                                field.onChange(false);
                                            }}
                                        >
                                            No
                                        </Button>
                                    </>
                                )}
                            />
                        )}
                    </Grid>
                    {showFollowUpQuestion && watchLastDoctorVisitReasonAddnTests && (
                        <Grid item xs={12}>
                            <Typography className={commonClasses.label}>Please Provide Additional Details</Typography>
                            <Controller
                                name="lastDoctorVisitReasonAddnTestsDetails"
                                control={control}
                                render={({field, fieldState: {error}}) => (
                                    <InputText
                                        {...field}
                                        aria-label="Please Provide Additional Details"
                                        placeholder="Please Provide Additional Details"
                                        error={!!error}
                                        helperText={error?.message}
                                        classes={{root: commonClasses.input}}
                                    />
                                )}
                            />
                        </Grid>
                    )}
                    {watchHasSeenPrimaryCare && !showFollowUpQuestion && (
                        <>
                            <Grid item xs={12}>
                                <Typography className={commonClasses.label}>Reason for Visit</Typography>
                                <Controller
                                    name={'reasonForVisit'}
                                    control={control}
                                    render={({field, fieldState: {error}}) => (
                                        <>
                                            <Select
                                                {...field}
                                                fullWidth
                                                aria-label="Reason for Visit"
                                                variant="outlined"
                                                displayEmpty
                                                error={!!error}
                                                className={commonClasses.select}
                                                IconComponent={ExpandMoreIcon}
                                                onChange={(e) => field.onChange(e.target.value)}
                                            >
                                                <MenuItem value="" disabled>
                                                    Reason for Visit
                                                </MenuItem>
                                                {[
                                                    'Routine annual wellness check up',
                                                    'Follow up on labwork or testing',
                                                    'Medical condition previously disclosed',
                                                    'Other medical condition not previously disclosed',
                                                ].map((value) => (
                                                    <MenuItem key={value} value={value}>
                                                        {value}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {!!error && (
                                                <FormHelperText error={!!error} style={{margin: '3px 14px'}}>
                                                    {error?.message}
                                                </FormHelperText>
                                            )}
                                        </>
                                    )}
                                />
                            </Grid>
                            {watchReasonForVisit && !showFollowUpQuestion && (
                                <>
                                    <Grid item xs={12} style={{paddingTop: 20, paddingBottom: 20}}>
                                        <Divider />
                                    </Grid>
                                    <Grid item xs={8} ref={containerRef}>
                                        <Typography className={commonClasses.label}>Provider Address</Typography>
                                        <Controller
                                            name="providerAddressLine"
                                            control={control}
                                            render={({field, fieldState: {error}}) => (
                                                <>
                                                    {isLoaded && (
                                                        <Autocomplete
                                                            onLoad={handleLoad}
                                                            onUnmount={() => setAutocomplete(null)}
                                                            options={{
                                                                componentRestrictions: {country: 'us'},
                                                            }}
                                                            onPlaceChanged={onPlacesChanged}
                                                            types={['hospital']}
                                                        >
                                                            <InputText
                                                                {...field}
                                                                aria-label="Provider Street Address"
                                                                placeholder="Provider Street Address"
                                                                error={!!error}
                                                                helperText={error?.message}
                                                                classes={{root: commonClasses.input}}
                                                            />
                                                        </Autocomplete>
                                                    )}
                                                    {loadError && (
                                                        <InputText
                                                            {...field}
                                                            aria-label="Provider Street Address"
                                                            placeholder="Provider Street Address"
                                                            error={!!error}
                                                            helperText={error?.message}
                                                            classes={{root: commonClasses.input}}
                                                        />
                                                    )}
                                                </>
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={4} className={commonClasses.input}>
                                        <Typography className={commonClasses.label}>Date Visited</Typography>
                                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                            <Controller
                                                name="visitDate"
                                                control={control}
                                                render={({field, fieldState: {error}}) => (
                                                    <KeyboardDatePicker
                                                        {...field}
                                                        inputVariant="outlined"
                                                        fullWidth
                                                        aria-label="Date Visited"
                                                        variant="inline"
                                                        format="MM/dd/yyyy"
                                                        value={field.value || null}
                                                        maxDate={new Date()}
                                                        onChange={(date) => setValue('visitDate', date)}
                                                        onBlur={() => trigger('visitDate')}
                                                        keyboardIcon={<DateRange />}
                                                        placeholder="--"
                                                        disableToolbar
                                                        error={!!error}
                                                        helperText={error?.message}
                                                    />
                                                )}
                                            />
                                        </MuiPickersUtilsProvider>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography className={commonClasses.label}>City</Typography>
                                        <Controller
                                            name="providerCity"
                                            control={control}
                                            render={({field, fieldState: {error}}) => (
                                                <InputText
                                                    {...field}
                                                    aria-label="Provider City"
                                                    placeholder="City"
                                                    error={!!error}
                                                    helperText={error?.message}
                                                    classes={{root: commonClasses.input}}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography className={commonClasses.label}>State</Typography>
                                        <Controller
                                            name={'providerState'}
                                            control={control}
                                            render={({field, fieldState: {error}}) => (
                                                <>
                                                    <Select
                                                        {...field}
                                                        fullWidth
                                                        aria-label="Provider State"
                                                        variant="outlined"
                                                        displayEmpty
                                                        error={!!error}
                                                        className={commonClasses.select}
                                                        IconComponent={ExpandMoreIcon}
                                                    >
                                                        {STATE_OPTIONS.map((value) => (
                                                            <MenuItem key={value} value={value}>
                                                                {value}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    {!!error && (
                                                        <FormHelperText error={!!error} style={{margin: '3px 14px'}}>
                                                            {error?.message}
                                                        </FormHelperText>
                                                    )}
                                                </>
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography className={commonClasses.label}>Zip Code</Typography>
                                        <Controller
                                            name="providerZipCode"
                                            control={control}
                                            render={({field, fieldState: {error}}) => (
                                                <InputText
                                                    {...field}
                                                    aria-label="Provider Zip Code"
                                                    placeholder="Zip Code"
                                                    error={!!error}
                                                    helperText={error?.message}
                                                    mask={{mask: [{mask: '00000'}, {mask: '00000 - 0000'}]}}
                                                    onChange={(e) => setValue('providerZipCode', e.target.value)}
                                                    onBlur={() => trigger('providerZipCode')}
                                                    classes={{root: commonClasses.input}}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} style={{paddingTop: 16, paddingBottom: 16}}>
                                        <Divider />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography className={commonClasses.label}>
                                            Name of Medical Professional
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography className={commonClasses.label}>First Name (Optional)</Typography>
                                        <Controller
                                            name="providerFirstName"
                                            control={control}
                                            render={({field, fieldState: {error}}) => (
                                                <InputText
                                                    {...field}
                                                    aria-label="First Name"
                                                    placeholder="First Name"
                                                    error={!!error}
                                                    helperText={error?.message}
                                                    classes={{root: commonClasses.input}}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography className={commonClasses.label}>Last Name</Typography>
                                        <Controller
                                            name="providerLastName"
                                            control={control}
                                            render={({field, fieldState: {error}}) => (
                                                <InputText
                                                    {...field}
                                                    aria-label="Last Name"
                                                    placeholder="Last Name"
                                                    error={!!error}
                                                    helperText={error?.message}
                                                    classes={{root: commonClasses.input}}
                                                />
                                            )}
                                        />
                                    </Grid>
                                </>
                            )}
                        </>
                    )}
                    <FixedNavigation
                        onBack={() => {
                            if (showFollowUpQuestion) {
                                setShowFollowUpQuestion(false);
                                setValue('lastDoctorVisitReasonAddnTests', null);
                                setValue('lastDoctorVisitReasonAddnTestsDetails', '');
                                setTimeout(() => trigger(), 100);
                            } else if (onBack) onBack();
                        }}
                        isNextDisabled={!formState.isValid}
                        isLoading={formState.isSubmitting}
                    />
                </Grid>
            </Box>
        );
    },
);

export default LastDoctorVisit;
